/**
 * @fileoverview PageTitle component for rendering breadcrumb-style navigation
 */
import React, { FunctionComponent } from 'react';
import { Flex, PageTitleNavLink } from 'basecamp';
import { useLocation } from 'react-router-dom';

/**
 * Props interface for PageTitle component
 * @interface Props
 * @property {string} [pageTitle] - Optional custom page title to display
 * @property {Routes.Route[]} [routes] - Optional array of route configuration objects
 */
type Props = {
	/** Page title */
	pageTitle?: string;
	/** Array of route configuration objects */
	routes?: Routes.Route[];
};

/**
 * PageTitle Component
 *
 * Renders a breadcrumb-style navigation component showing the current page title.
 * The title can either be provided directly via props, or derived from the current route.
 *
 * If a route's baseName is provided, it will show as the first breadcrumb level,
 * followed by the route's displayName. Otherwise just the displayName is shown.
 * @example
 * // Basic usage with custom page title
 * <PageTitle pageTitle="My Custom Page" />
 *
 * // Usage with routes configuration
 * const routes = [{
 *   path: '/dashboard',
 *   displayName: 'Dashboard',
 *   baseName: 'Home'
 * }];
 * <PageTitle routes={routes} />
 * @component
 * @param {Props} props - Component props
 * @param {string} [props.pageTitle] - Optional custom page title to display
 * @param {Routes.Route[]} [props.routes] - Optional array of route configuration objects
 * @returns {JSX.Element} Rendered page title navigation component
 */
const PageTitle: FunctionComponent<Props> = ({ routes, pageTitle }) => {
	const location = useLocation();
	const getCurrentRoute = routes?.find((route) => route.path === location.pathname);
	const getBreadcrumbTitle = [getCurrentRoute?.baseName, getCurrentRoute?.displayName].filter(
		Boolean
	);

	/**
	 * Renders the page title component content
	 *
	 * If a pageTitle prop is provided, renders it directly.
	 * Otherwise renders a breadcrumb-style navigation using the baseName and displayName from the current route.
	 * For breadcrumb navigation, shows a divider between items if there are multiple levels.
	 *
	 * @function
	 * @returns {JSX.Element|JSX.Element[]} Either a single PageTitleNavLink or array of PageTitleNavLinks for breadcrumbs
	 */
	const renderComponent = () => {
		// If pageTitle is provided, render it
		if (!!pageTitle) {
			return <PageTitleNavLink>{decodeURIComponent(pageTitle)}</PageTitleNavLink>;
		}

		// If pageTitle is not provided, render the breadcrumb title
		return getBreadcrumbTitle.map((item, index) => {
			// If the current item is the first item, don't show the divider
			const showDivider = getBreadcrumbTitle.length > 1 && index === 0;

			return (
				<PageTitleNavLink key={`${item}-${index}`} showMenuDivider={showDivider}>
					{item}
				</PageTitleNavLink>
			);
		});
	};

	/**
	 * Renders the page title component
	 *
	 * @returns {JSX.Element} Rendered page title component
	 */
	return (
		<Flex mx={4} mb={3}>
			{renderComponent()}
		</Flex>
	);
};

export default PageTitle;
